// Typed transport for the AI Home Decorator HTTP API. Mirrors APIClient.swift.
// Session token lives in localStorage; on 401 we invoke a registered re-auth
// handler (Google sign-in) once and retry. See docs/API.md.

import type {
  AuthUser,
  Job,
  SyncPushRecord,
  SyncRecord,
} from "./types";
import { buildCreateObjectBody, type CaptureMode } from "./captureModes";

const API_URL: string = (import.meta.env.VITE_API_URL as string) || "";
const TOKEN_KEY = "aihd.sessionToken";

let inMemoryToken: string | null = null;
let reauth: (() => Promise<string | null>) | null = null;

export function getToken(): string | null {
  if (inMemoryToken) return inMemoryToken;
  try {
    inMemoryToken = localStorage.getItem(TOKEN_KEY);
  } catch {
    inMemoryToken = null;
  }
  return inMemoryToken;
}

export function setToken(token: string | null): void {
  inMemoryToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage may be unavailable */
  }
}

/** Register a callback that obtains a fresh session token (e.g. silent Google re-auth). */
export function setReauthHandler(fn: (() => Promise<string | null>) | null): void {
  reauth = fn;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  auth?: boolean; // attach bearer token (default true)
  headers?: Record<string, string>;
  retrying?: boolean;
  /** Path shown in ApiError messages (redacts secrets like invite tokens). */
  errorLabel?: string;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, auth = true, headers = {} } = opts;
  const h: Record<string, string> = { ...headers };
  if (body !== undefined) h["content-type"] = "application/json";
  if (auth) {
    const t = getToken();
    if (t) h["authorization"] = `Bearer ${t}`;
  }
  const resp = await fetch(API_URL + path, {
    method,
    headers: h,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 401 && auth && !opts.retrying && reauth) {
    const fresh = await reauth();
    if (fresh) {
      setToken(fresh);
      return request<T>(path, { ...opts, retrying: true });
    }
  }

  const text = await resp.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!resp.ok) {
    const code =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as Record<string, unknown>).error)
        : undefined;
    throw new ApiError(
      resp.status,
      code,
      `${method} ${opts.errorLabel ?? path} → ${resp.status} ${code ?? ""}`,
    );
  }
  return parsed as T;
}

// --- Auth ---
export async function authGoogle(idToken: string): Promise<{ sessionToken: string; user: AuthUser }> {
  return request("/v1/auth/google", { method: "POST", auth: false, body: { idToken } });
}

export async function authApple(
  identityToken: string,
  name?: string,
): Promise<{ sessionToken: string; user: AuthUser }> {
  return request("/v1/auth/apple", { method: "POST", auth: false, body: { identityToken, name } });
}

export async function fetchMe(): Promise<AuthUser> {
  const r = await request<{ user: AuthUser }>("/v1/auth/me");
  return r.user;
}

// --- Sync ---
export interface SyncPullPage {
  records: SyncRecord[];
  nextCursor: string;
  hasMore: boolean;
}
/** Pull a delta page. `scope` targets a shared project ("project:<pid>"); omit for own records. */
export async function syncPull(since: string, limit = 300, scope?: string): Promise<SyncPullPage> {
  const qs = new URLSearchParams({ since, limit: String(limit) });
  if (scope) qs.set("scope", scope);
  return request(`/v1/sync?${qs.toString()}`);
}

/** Per-record push rejection (viewer writes, editor PROJECT deletes, non-members). */
export interface SyncPushRejection {
  entityType: string;
  entityId: string;
  reason: string; // owner_only | forbidden_role | not_a_member | ...
}
export interface SyncPushResult {
  applied: { entityType: string; entityId: string; version: number; updatedAt: string }[];
  conflicts: SyncRecord[];
  cursor: string;
  /** Additive (sharing-aware backends only) — absent on old backends. */
  rejected?: SyncPushRejection[];
}
export async function syncPush(records: SyncPushRecord[]): Promise<SyncPushResult> {
  return request("/v1/sync", { method: "POST", body: { records } });
}

// --- Splat (photoreal) jobs ---
export interface SplatModelVariant {
  id: string;
  label: string;
  bytes: number | null;
  url: string;
}
export interface SplatJob {
  splatId: string;
  status: string;
  progress: number | null;
  modelUrl?: string;
  models?: SplatModelVariant[];
  roomScanId?: string | null;   // W4: paired RoomPlan scan (for the dollhouse)
  alignmentUrl?: string;        // W4: presigned alignment.json (splat->RoomPlan transform)
  meshUrl?: string;             // W4: presigned room-mesh.glb (textured polygon mesh from the splat)
  error?: string;
}
/** GET /v1/splats/{id} — status + (on completion) a presigned model.ply URL + variant list. */
export async function getSplatJob(id: string): Promise<SplatJob> {
  return request<SplatJob>(`/v1/splats/${encodeURIComponent(id)}`);
}

/**
 * POST /v1/splats/from-assets — create a pure-image (COLMAP→splatfacto) photoreal model from a
 * set of the caller's existing image blobs (a room's photos). Server writes a manifest + launches
 * the trainer; no upload needed (the blobs already exist). Needs >= 8 unique keys.
 */
export async function createSplatFromAssets(
  keys: string[],
): Promise<{ splatId: string; status: string; imageCount: number }> {
  return request<{ splatId: string; status: string; imageCount: number }>("/v1/splats/from-assets", {
    method: "POST",
    body: { keys },
  });
}

export interface SplatListItem {
  splatId: string;
  type: string; // "splat" | "image-splat"
  status: string;
  createdAt?: string;
  updatedAt?: string;
  inputKind?: string | null; // "video" | "images" for image-splat
  label?: string | null; // human label if the job carries one (A/B, HQ re-runs, "from photos")
  source?: string | null; // e.g. "photos" for a from-assets job
  roomScanId?: string | null; // W4: paired RoomPlan scan, if any
}
/** GET /v1/splats — the caller's own splat jobs (incl. local-only image-splat / Rich Capture). */
export async function listSplats(): Promise<SplatListItem[]> {
  const r = await request<{ jobs: SplatListItem[] }>(`/v1/splats`);
  return r.jobs ?? [];
}

/** POST /v1/splats/{id}/pair — pair a photoreal model with the RoomPlan scan of the same room. */
export async function pairSplat(id: string, roomScanId: string): Promise<{ splatId: string; roomScanId: string }> {
  return request(`/v1/splats/${encodeURIComponent(id)}/pair`, { method: "POST", body: { roomScanId } });
}

// --- Object capture (walk-around video/photos → foreground-only object splat) ---
export type ObjectJobStatus = "awaiting-upload" | "queued" | "processing" | "completed" | "failed";

/** One media file declared to POST /v1/objects (uploads[i] in the response matches media[i]). */
export interface ObjectMediaInput {
  kind: "video" | "image";
  contentType: string; // video/mp4 | video/quicktime | image/jpeg | image/png
  fileName?: string;
}

export interface ObjectUploadTarget {
  key: string;
  url: string; // presigned S3 PUT — must PUT with exactly `contentType`
  contentType: string;
}

export interface ObjectJob {
  objectId: string;
  status: ObjectJobStatus;
  stage?: string;
  pct?: number;
  detail?: string;
  label?: string;
  captureMode?: "object" | "scene" | "subject";
  createdAt?: string;
  updatedAt?: string;
  error?: string;
  modelUrl?: string;
  models?: SplatModelVariant[];
  metaUrl?: string;
  posterUrl?: string;
  meshUrl?: string;
}

export interface ObjectListItem {
  objectId: string;
  status: ObjectJobStatus;
  stage?: string;
  pct?: number;
  label?: string;
  captureMode?: "object" | "scene" | "subject";
  createdAt?: string;
  updatedAt?: string;
  posterUrl?: string;
}

/** object-meta.json (fetched from ObjectJob.metaUrl) — viewer camera framing, in the PLY frame. */
export interface ObjectMeta {
  version?: number;
  captureMode?: string;
  /** Scene-mode diagnostics; pathCentroid homes the camera on the CAPTURED side. */
  scene?: { pathCentroid?: [number, number, number] };
  center: [number, number, number];
  up: [number, number, number];
  radius: number;
  bbox?: { min: [number, number, number]; max: [number, number, number] };
  counts?: { trained?: number; final?: number };
}

/** POST /v1/objects — declare the media set; returns presigned PUT targets (60 min). */
export async function createObject(
  media: ObjectMediaInput[],
  label?: string,
  captureMode?: CaptureMode,
): Promise<{ objectId: string; uploads: ObjectUploadTarget[] }> {
  return request("/v1/objects", {
    method: "POST",
    body: buildCreateObjectBody(media, label, captureMode),
  });
}

/** POST /v1/objects/{id}/start — verify uploads + launch the GPU box. */
export async function startObject(id: string): Promise<{ objectId: string; status: ObjectJobStatus }> {
  return request(`/v1/objects/${encodeURIComponent(id)}/start`, { method: "POST" });
}

export async function getObjectJob(id: string): Promise<ObjectJob> {
  return request<ObjectJob>(`/v1/objects/${encodeURIComponent(id)}`);
}

export async function listObjects(): Promise<ObjectListItem[]> {
  const r = await request<{ objects: ObjectListItem[] }>("/v1/objects");
  return r.objects ?? [];
}

/**
 * PUT a file to a presigned URL with UPLOAD progress (a 170 MB walk-around video is normal).
 * XMLHttpRequest because fetch has no upload-progress events. Rejects on non-2xx.
 */
export function putFileWithProgress(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("content-type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.total > 0 ? e.loaded / e.total : 0);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status, undefined, `PUT upload → ${xhr.status}`));
    };
    xhr.onerror = () => reject(new ApiError(0, undefined, "PUT upload → network error"));
    xhr.onabort = () => reject(new ApiError(0, undefined, "PUT upload → aborted"));
    xhr.send(file);
  });
}

// --- Assets ---
export interface PresignPutAsset {
  sha256: string;
  status: "present" | "missing";
  key: string;
  uploadUrl?: string;
}
/**
 * Presign PUT uploads. Pass `projectId` when uploading blobs for a SHARED project —
 * the server then presigns into the project owner's prefix so the blobs stay with
 * the project (survive the editor's later departure). Omit for own projects.
 */
export async function presignPutAssets(
  assets: { sha256: string; bytes: number; contentType: string; kind: string }[],
  projectId?: string,
): Promise<PresignPutAsset[]> {
  const r = await request<{ assets: PresignPutAsset[] }>("/v1/assets", {
    method: "POST",
    body: projectId ? { assets, projectId } : { assets },
  });
  return r.assets;
}

export interface PresignGetAsset {
  key: string;
  url?: string;
  bytes?: number;
  status: "present" | "missing";
}
export async function presignGetAssets(keys: string[]): Promise<PresignGetAsset[]> {
  const r = await request<{ assets: PresignGetAsset[] }>("/v1/assets/batch", {
    method: "POST",
    body: { keys },
  });
  return r.assets;
}

// --- Uploads (net-new browser photos) ---
export async function requestUploads(
  contentTypes: string[],
): Promise<{ key: string; url: string }[]> {
  const r = await request<{ uploads: { key: string; url: string }[] }>("/v1/uploads", {
    method: "POST",
    body: { files: contentTypes.map((contentType) => ({ contentType })) },
  });
  return r.uploads;
}

// --- Render jobs ---
export async function createJob(input: {
  room: { id: string; name: string };
  goal: string;
  sourceKeys: string[];
  feedback?: string;
  previousJobId?: string;
  // Backend-owns-results: the worker finalizes the makeover into the sync graph
  // on these exact ids, so every client just pulls the result.
  sessionId?: string;
  cycleId?: string;
  cycleIndex?: number;
  sourceFileNames?: string[];
  sourceAssets?: unknown[];
}): Promise<{ jobId: string; status: string }> {
  return request("/v1/jobs", { method: "POST", body: input });
}

export async function getJob(jobId: string): Promise<Job> {
  return request(`/v1/jobs/${encodeURIComponent(jobId)}`);
}

// --- Project sharing (docs/API.md §v3) ---
// Old-backend degrade: every route here 404s on a pre-sharing backend; callers
// treat that as "sharing isn't available yet". Invite tokens are secrets: they
// never appear in ApiError messages (errorLabel) and must never be logged.

export type ProjectRole = "owner" | "editor" | "viewer";

/** One shared-project membership of the CALLER (GET /v1/memberships). */
export interface Membership {
  projectId: string;
  role: ProjectRole;
  ownerUserId: string;
  ownerName: string | null;
  projectName: string | null;
  addedAt?: string;
}
/**
 * All the caller's memberships. NOTE: includes the caller's own `role:"owner"`
 * rows for projects they've shared — clients skip those for scope pulls and
 * membership maps (own projects arrive via the unscoped pull).
 */
export async function fetchMemberships(): Promise<Membership[]> {
  const r = await request<{ memberships: Membership[] }>("/v1/memberships");
  return r.memberships ?? [];
}

export interface ProjectInvite {
  token: string;
  url: string;
  role: ProjectRole;
  expiresAt: string;
  revokedAt?: string | null;
  status?: string; // active | expired | revoked
}
/** Owner only. Creates a shareable invite link carrying role + expiry. */
export async function createInvite(
  projectId: string,
  role: "editor" | "viewer",
  expiresInDays = 7,
): Promise<ProjectInvite> {
  const r = await request<{ invite: ProjectInvite }>(
    `/v1/projects/${encodeURIComponent(projectId)}/invites`,
    { method: "POST", body: { role, expiresInDays } },
  );
  return r.invite;
}

/** Owner only. Lists the project's invites (incl. revoked/expired, per `status`). */
export async function listInvites(projectId: string): Promise<ProjectInvite[]> {
  const r = await request<{ invites: ProjectInvite[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/invites`,
  );
  return r.invites ?? [];
}

/** Owner only. */
export async function revokeInvite(token: string): Promise<void> {
  await request(`/v1/invites/${encodeURIComponent(token)}/revoke`, {
    method: "POST",
    errorLabel: "/v1/invites/<token>/revoke",
  });
}

/** UNAUTHENTICATED invite preview for the join landing page. */
export interface InvitePreview {
  projectName: string | null;
  inviterName: string | null;
  role: ProjectRole;
  expiresAt: string;
  status: string; // active | expired | revoked
}
export async function getInvitePreview(token: string): Promise<InvitePreview> {
  return request(`/v1/invites/${encodeURIComponent(token)}`, {
    auth: false,
    errorLabel: "/v1/invites/<token>",
  });
}

export interface RedeemResult {
  projectId: string;
  role: ProjectRole;
  ownerUserId: string;
  ownerName: string | null;
  projectName: string | null;
  alreadyMember?: boolean;
}
export async function redeemInvite(token: string): Promise<RedeemResult> {
  return request(`/v1/invites/${encodeURIComponent(token)}/redeem`, {
    method: "POST",
    errorLabel: "/v1/invites/<token>/redeem",
  });
}

export interface ProjectMember {
  userId: string;
  email: string | null;
  name: string | null;
  role: ProjectRole;
  addedAt?: string;
}
/** Any member. */
export async function listMembers(projectId: string): Promise<ProjectMember[]> {
  const r = await request<{ members: ProjectMember[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/members`,
  );
  return r.members ?? [];
}

/** Owner only. */
export async function removeMember(projectId: string, userId: string): Promise<void> {
  await request(
    `/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}/remove`,
    { method: "POST" },
  );
}

/** Member (not owner). Caller then purges the project locally. */
export async function leaveProject(projectId: string): Promise<void> {
  await request(`/v1/projects/${encodeURIComponent(projectId)}/leave`, { method: "POST" });
}

// --- Raw S3 PUT to a presigned URL (no auth header; CT must match the signed CT) ---
export async function putBytes(url: string, blob: Blob, contentType: string): Promise<void> {
  const resp = await fetch(url, {
    method: "PUT",
    headers: { "content-type": contentType },
    body: blob,
  });
  if (!resp.ok) throw new ApiError(resp.status, undefined, `PUT blob → ${resp.status}`);
}

export const apiBaseUrl = API_URL;
